import { DEFAULT_LOCALE, isSupportedLocale, type Locale } from './locales';

/**
 * Locale preference persistence — the framework-agnostic core, mirroring
 * `@ecom/ui-kit`'s own `theme.ts` split (a dependency-free read/persist
 * pair, plus a separate React binding in `use-locale-preference.ts`) so
 * the same logic never has to be re-derived in two places.
 *
 * Only for apps with no server-driven locale (the merchant and admin
 * dashboards — Vite SPAs with a single `index.html`, no route segment to
 * read a locale from). The storefront's locale comes from its `[locale]`
 * URL segment instead and has no use for this file.
 */
export const LOCALE_STORAGE_KEY = 'ecom-locale';

/** Never throws: storage can be disabled (private browsing, quota) or absent — both return null, not a crash. */
export function readStoredLocale(storage?: Pick<Storage, 'getItem'>): Locale | null {
  try {
    const target = storage ?? globalThis.localStorage;
    const value = target.getItem(LOCALE_STORAGE_KEY);
    return value !== null && isSupportedLocale(value) ? value : null;
  } catch {
    return null;
  }
}

export function persistLocale(locale: Locale, storage?: Pick<Storage, 'setItem'>): void {
  try {
    const target = storage ?? globalThis.localStorage;
    target.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Degrades to "applies for this page load only" — acceptable, the same
    // posture persistTheme() already takes.
  }
}

export function resolveInitialLocale(storage?: Pick<Storage, 'getItem'>): Locale {
  return readStoredLocale(storage) ?? DEFAULT_LOCALE;
}
